import React from 'react';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { VACCINATION_RECORD } from '../../../../shared/services/api/endpoints';
import { StatusBadge } from '../../../../shared/components/badges/StatusBadge';
import VaccineRecordExpandRow from '../components/VaccineRecordExpandRow';
import TabbedForm from '../../../../shared/components/forms/FormTabs';
import apiService from '../../../../shared/services/apiService';
import {
  CenterService,
  AppointmentService
} from '../../../../shared/services';


const formatDate = (value, withTime = false) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime && { hour: '2-digit', minute: '2-digit' })
  });
};



export const vaccineRecordConfig = {
  title: 'Vaccination Records',
  endpoint: 'admin/vaccination-records',
  icon: ClipboardDocumentListIcon,
  useUrlNavigation: true,
  basePath: '/admin/vaccination-records',
  showAddButton: false,
  showEditButton: false,
  showDeleteButton: false,



  /**
   * ======================
   * TABLE COLUMNS (SAFE)
   * ======================
   */
  columns: [
    {
      key: 'childName',
      label: 'Child Name',
      render: (item) => item.child?.name || '—'
    },
    {
      key: 'vaccineName',
      label: 'Vaccine',
      render: (item) => item.vaccine?.name || item.vaccineName || '—'
    },
    {
      key: 'doseLabel',
      label: 'Dose',
      render: (item) => item.doseLabel || `${item.doseNumber || '—'}`
    },
    {
      key: 'administeredDate',
      label: 'Administered Date',
      render: (item) => (
        <span>
          {item.administeredDate ? new Date(item.administeredDate).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }) : '—'}
        </span>
      )
    },
    {
      key: 'administeredBy',
      label: 'Administered By',
      render: (item) => item.administeredBy?.name || item.administeredBy?.fullName || '—'
    },
    // {
    //   key: 'vaccinationCenter',
    //   label: 'Center',
    //   render: (item) => item.vaccinationCenter?.name || '—'
    // },
    {
      key: 'appointmentStatus',
      label: 'Status',
      render: (item) => <StatusBadge status={item.appointment?.status || item.status} />
    }
  ],

  formFields: [
  // 👶 CHILD INFO
  {
    label: 'Child Name',
    section: 'child',
    renderValue: (_, item) => item?.child?.name || '—'
  },
  {
    label: 'Date of Birth',
    section: 'child',
    renderValue: (_, item) => formatDate(item?.child?.dateOfBirth)
  },
  {
    label: 'Gender',
    section: 'child',
    renderValue: (_, item) => item?.child?.gender || '—'
  },
  {
    label: 'Profile Photo',
    section: 'child',
    renderValue: (_, item) =>
      item?.child?.profilePhoto ? 'Available' : 'Not Available'
  },

  // 👨‍👩‍👧 PARENT INFO
  {
    label: 'Parent Name',
    section: 'child',
    renderValue: (_, item) => item?.child?.parent?.fullName || '—'
  },
  {
    label: 'Parent Phone',
    section: 'child',
    renderValue: (_, item) => item?.child?.parent?.phone || '—'
  },
  {
    label: 'Parent Email',
    section: 'child',
    renderValue: (_, item) => item?.child?.parent?.email || '—'
  },

  // 💉 VACCINE INFO
  {
    label: 'Vaccine Name',
    section: 'vaccination',
    renderValue: (_, item) => item?.vaccine?.name || '—'
  },
  {
    label: 'Vaccine Description',
    section: 'vaccination',
    renderValue: (_, item) => item?.vaccine?.description || '—'
  },
  {
    label: 'Dose Number',
    section: 'vaccination',
    renderValue: (_, item) => item?.doseLabel || item?.doseNumber || '—'
  },
  // {
  //   label: 'Batch Number',
  //   section: 'vaccination',
  //   renderValue: (_, item) => item?.batchNumber || '—'
  // },
  {
    label: 'Administered Date',
    section: 'vaccination',
    renderValue: (_, item) => formatDate(item?.administeredDate, true)
  },
  {
    label: 'Next Due Date',
    section: 'vaccination',
    renderValue: (_, item) => formatDate(item?.nextDueDate)
  },

  // 🏥 ADMINISTRATION
 {
  label: 'Administered By',
  section: 'administration',
  renderValue: (_, item) =>
    item?.medicalStaff?.fullName || '—'
},
{
  label: 'Medical Staff Role',
  section: 'administration',
  renderValue: (_, item) =>
    item?.medicalStaff?.role || '—'
},
{
  label: 'Specialization',
  section: 'administration',
  renderValue: (_, item) =>
    item?.medicalStaff?.specialization || '—'
},

  {
    label: 'Completed by Medical Staff',
    section: 'administration',
    renderValue: (_, item) => item?.completedByMedicalStaff ? 'Yes' : 'No'
  },
  {
    label: 'Vaccination Center',
    section: 'administration',
    renderValue: (_, item) => item?.vaccinationCenter?.name || '—'
  },
  {
    label: 'Center Address',
    section: 'administration',
    renderValue: (_, item) => item?.vaccinationCenter?.address || '—'
  },
  {
    label: 'Center Phone',
    section: 'administration',
    renderValue: (_, item) => item?.vaccinationCenter?.phone || '—'
  },

  // 📅 APPOINTMENT
  {
    label: 'Scheduled Date',
    section: 'appointment',
    renderValue: (_, item) => formatDate(item?.appointment?.scheduledDate)
  },
  {
    label: 'Scheduled Time',
    section: 'appointment',
    renderValue: (_, item) => item?.appointment?.scheduledTime || '—'
  },
  {
    label: 'Appointment Status',
    section: 'appointment',
    renderValue: (_, item) => item?.appointment?.status || '—'
  },

  // 🧾 NOTES
  {
    label: 'Reactions',
    section: 'notes',
    renderValue: (_, item) => item?.reactions || '—'
  },
  {
    label: 'Additional Notes',
    section: 'notes',
    renderValue: (_, item) => item?.notes || '—'
  },
  {
    label: 'Created At',
    section: 'notes',
    renderValue: (_, item) => formatDate(item?.createdAt, true)
  },
  {
    label: 'Updated At',
    section: 'notes',
    renderValue: (_, item) => formatDate(item?.updatedAt, true)
  }
]
,
  searchFields: ['vaccineName'],

  formState: { activeTab: 'vaccination' },

  customHandlers: {
   transformData: (response) => {
  const items =
    response?.data?.vaccinationRecords ||
    response?.data?.data?.vaccinationRecords ||
    response?.data?.records ||
    response?.data?.data?.records ||
    response?.data ||
    response?.vaccinationRecords ||
    response?.records ||
    [];

  if (!Array.isArray(items)) return [];

  // ✅ NORMALIZE administeredBy
  return items.map(item => ({
    ...item,
    administeredBy:
      item.administeredBy ||
      item?.appointment?.administeredBy ||
      null
  }));
},

    renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab) => {
      return <TabbedForm formFields={formFields} formData={formData} handleFormChange={handleFormChange} renderFormField={renderFormField}  />;
    },
    renderExpandedRow: (item) => <VaccineRecordExpandRow item={item} />,
    processFormData: (formData) => ({
      ...formData
    })
  }
};
